import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "react-hot-toast";

import { CreatePollForm } from "@/components/forms/create-poll-form";
import type { CreatePollSchema } from "@/components/forms/create-poll-form";
import { PollResults } from "@/components/poll-results";
import { Button } from "@/components/ui/button";
import { apiClient, apiErrorMessage } from "@/lib/api";
import { getPublicNowPlaying } from "@/lib/explore";
import { episodePollsQuery } from "@/lib/polls";

const NOW_PLAYING_REFRESH_MS = 60_000;

function EpisodePolls({
  episodeId,
  title,
}: {
  episodeId: string;
  title: string;
}) {
  const queryClient = useQueryClient();
  const polls = useQuery(episodePollsQuery(episodeId));
  const [stopping, setStopping] = useState(false);
  const activePoll = polls.data?.find((poll) => poll.isActive);
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: episodePollsQuery(episodeId).queryKey,
    });

  const create = async (values: CreatePollSchema) => {
    try {
      await apiClient.post("/api/polls", {
        episodeId,
        isActive: true,
        options: values.options.map((option, position) => ({
          label: option.content,
          position,
        })),
        question: values.question,
      });
      await refresh();
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  };

  const stop = async () => {
    if (!activePoll) {
      return;
    }
    setStopping(true);
    try {
      await apiClient.patch(`/api/polls/${activePoll.id}`, {
        isActive: false,
      });
      await refresh();
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Polls</h1>
        <p className="text-muted-foreground">{title}</p>
      </header>

      {polls.isPending && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {activePoll && (
        <section className="flex flex-col gap-5 rounded-2xl border p-5">
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-lg font-semibold text-balance">
              {activePoll.question}
            </h2>
            <Button
              disabled={stopping}
              onClick={stop}
              size="sm"
              variant="outline"
            >
              Stop poll
            </Button>
          </div>
          <PollResults poll={activePoll} />
        </section>
      )}

      {polls.isSuccess && !activePoll && <CreatePollForm process={create} />}
    </div>
  );
}

const Polls = () => {
  const episode = useQuery({
    queryFn: getPublicNowPlaying,
    queryKey: ["episodes", "now"] as const,
    refetchInterval: NOW_PLAYING_REFRESH_MS,
  });

  if (episode.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  if (!episode.data) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Polls</h1>
        <p className="text-muted-foreground">
          Polls open while an episode is on air.
        </p>
      </div>
    );
  }

  return (
    <EpisodePolls
      episodeId={episode.data.id}
      key={episode.data.id}
      title={`${episode.data.showName} · ${episode.data.title}`}
    />
  );
};

export const Route = createFileRoute("/admin/polls")({
  component: Polls,
  head: () => ({
    meta: [{ title: "Polls" }],
  }),
});
